import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Ticket as TicketIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type DepartmentRef, type Paginated, type ProjectRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { anyFilter, useKeptFilters } from '../lib/kept-filters';
import { SlaPair } from '../components/SlaCountdown';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  RowLink,
  Select,
  Table,
  Td,
  Th,
  Tr,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

const TICKET_STATUSES = [
  'NEW', 'ASSIGNED', 'IN_PROGRESS', 'WAITING_REQUESTER', 'RESOLVED', 'CLOSED', 'REOPENED', 'CANCELLED',
];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const SLA_STATES = ['UNASSIGNED', 'AT_RISK', 'VIEW_BREACHED', 'ASSIGNMENT_BREACHED', 'OK'];

export function TicketsPage() {
  const { t, locale, localized } = useI18n();
  const { can, canAny } = useAuth();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  // Kept until cleared: opening a ticket and coming back finds the list as it was left.
  const [filters, setFilters] = useKeptFilters('tickets');

  const setFilter = (key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tickets', page, filters],
    queryFn: () => api.get<Paginated<any>>('/tickets', { page, pageSize: 25, ...filters }),
    // Countdowns are only as fresh as the last fetch.
    refetchInterval: 30_000,
  });

  // Every ticket raiser filters by department, `departments.read` or not; a
  // switched-off department still has its old tickets.
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker', 'all'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options', { includeInactive: true }),
  });

  const { data: categories } = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: () => api.get<any[]>('/ticket-categories'),
  });

  // The client list answers whoever raises or handles tickets (the API's guard); an
  // employee who only follows tickets they raised filters without it.
  const { data: clients } = useQuery({
    queryKey: ['client-options'],
    queryFn: () =>
      api.get<Array<{ id: string; code: string; companyName: string; companyNameAr: string | null }>>('/clients/options'),
    enabled: canAny(P.CLIENTS_VIEW, P.TICKETS_CREATE, P.TICKETS_HANDLE),
  });

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        count={data?.total}
        action={
          can(P.TICKETS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/tickets/new')}>
              {t('tickets.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => setFilter('q', e.target.value)}
          />
          <Select value={filters.clientId ?? ''} onChange={(e) => setFilter('clientId', e.target.value)}>
            <option value="">{t('tickets.client')} — {t('common.all')}</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <Select value={filters.priority ?? ''} onChange={(e) => setFilter('priority', e.target.value)}>
            <option value="">{t('common.priority')} — {t('common.all')}</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
          <Select value={filters.departmentId ?? ''} onChange={(e) => setFilter('departmentId', e.target.value)}>
            <option value="">{t('common.department')} — {t('common.all')}</option>
            {departments?.map((d) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>
          <Select value={filters.slaState ?? ''} onChange={(e) => setFilter('slaState', e.target.value)}>
            <option value="">{t('tickets.slaState')} — {t('common.all')}</option>
            {SLA_STATES.map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-2 border-t hairline px-4 py-2.5">
          <FilterChip active={filters.mine === 'true'} onClick={() => setFilter('mine', filters.mine === 'true' ? '' : 'true')}>
            {t('tickets.mine')}
          </FilterChip>
          <FilterChip
            active={filters.awaitingMyView === 'true'}
            onClick={() => setFilter('awaitingMyView', filters.awaitingMyView === 'true' ? '' : 'true')}
          >
            {t('tickets.awaitingMyView')}
          </FilterChip>
          <FilterChip
            active={filters.requestedByMe === 'true'}
            onClick={() => setFilter('requestedByMe', filters.requestedByMe === 'true' ? '' : 'true')}
          >
            {t('tickets.requestedByMe')}
          </FilterChip>
          <FilterChip
            active={filters.generalOnly === 'true'}
            onClick={() => setFilter('generalOnly', filters.generalOnly === 'true' ? '' : 'true')}
          >
            {t('tickets.generalOnly')}
          </FilterChip>
          <FilterChip
            active={filters.fromPortal === 'true'}
            onClick={() => setFilter('fromPortal', filters.fromPortal === 'true' ? '' : 'true')}
          >
            {t('tickets.fromPortal')}
          </FilterChip>
          {anyFilter(filters) && (
            <Button size="sm" variant="ghost" onClick={() => { setFilters({}); setPage(1); }}>
              {t('common.clear')}
            </Button>
          )}
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<TicketIcon className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('tickets.number')}</Th>
                  <Th>{t('tickets.subject')}</Th>
                  <Th>{t('common.department')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th>{t('common.assignee')}</Th>
                  <Th>SLA</Th>
                  <Th align="end">{t('common.createdAt')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((ticket) => (
                  <Tr key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)}>
                    <Td>
                      <RowLink to={`/tickets/${ticket.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                        {ticket.number}
                      </RowLink>
                    </Td>
                    <Td className="max-w-xs">
                      <span className="block truncate">{ticket.subject}</span>
                      <span className="block truncate text-caption text-faint">
                        {ticket.client
                          ? `${locale === 'ar' && ticket.client.companyNameAr ? ticket.client.companyNameAr : ticket.client.companyName} · `
                          : ''}
                        {ticket.contact ? `${t('tickets.requestedByName', { name: ticket.contact.name })} · ` : ''}
                        {ticket.project ? localized(ticket.project) : t('common.general')}
                      </span>
                      {ticket.requesterAccountId && (
                        <Badge tone="plasma" className="mt-1">{t('tickets.raisedOnPortal')}</Badge>
                      )}
                    </Td>
                    <Td className="text-muted">{localized(ticket.targetDepartment)}</Td>
                    <Td>
                      <Badge tone={statusTone(ticket.status)}>{enumLabel(ticket.status, locale)}</Badge>
                    </Td>
                    <Td>
                      <Badge tone={priorityTone(ticket.priority)}>{enumLabel(ticket.priority, locale)}</Badge>
                    </Td>
                    <Td className="text-muted">
                      {ticket.assignee ? localized(ticket.assignee) : (
                        <span className="text-faint">{t('common.unassigned')}</span>
                      )}
                    </Td>
                    <Td>
                      {['NEW', 'ASSIGNED'].includes(ticket.status) || !ticket.assignedAt ? (
                        <SlaPair
                          viewSeconds={ticket.viewSecondsRemaining}
                          assignmentSeconds={ticket.assignmentSecondsRemaining}
                          serverNow={ticket.serverNow}
                          viewMet={ticket.viewMet}
                          assignmentMet={ticket.assignmentMet}
                          stopped={ticket.status === 'CANCELLED'}
                          compact
                        />
                      ) : (
                        <span className="text-caption text-faint">—</span>
                      )}
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDateTime(ticket.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-pill px-3 py-1 text-caption font-medium ring-1 ring-inset transition-colors duration-fast ' +
        (active
          ? 'bg-primary/15 text-primary-600 dark:text-primary-400 ring-primary/40'
          : 'text-[var(--text-secondary)] ring-[var(--line)] hover:text-[var(--text-primary)]')
      }
    >
      {children}
    </button>
  );
}
